/**
 * Prototyper Service
 * 
 * Handles prototype creation and management for conversations
 */

import apiClient from '../client';
import { ApiUrls } from '../urls';
import type { 
  PrototypeCreationParams, 
  PrototypeCreationResponse,
  UpdatePrototyperDemoLinkParams,
  PrototyperBySlugResponse
} from '../../types/prototyper.types';

class PrototyperService {
  /**
   * Create a prototype from a conversation
   */
  async createPrototype(params: PrototypeCreationParams): Promise<PrototypeCreationResponse> {
    try {
      const { conversationId, projectId } = params;

      let url: string;

      // Determine which endpoint to use based on whether we have a project ID
      if (projectId) {
        // Use project conversation prototype endpoint
        url = ApiUrls.projectConversationPrototype(projectId, conversationId);
      } else {
        // Use temporary conversation prototype endpoint
        url = ApiUrls.tmpConversationPrototype(conversationId);
      }

      // Use extended timeout for prototype creation (10 minutes = 600000ms)
      // Prototyper can take more than 5 minutes to generate
      const response = await apiClient.post<PrototypeCreationResponse>(url, {}, {
        timeout: 600000, // 10 minutes
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a prototyper demo link (slug and/or header)
   */
  async updatePrototyperDemoLink(params: UpdatePrototyperDemoLinkParams): Promise<PrototypeCreationResponse> {
    try {
      const { id, slug} = params;
      const url = ApiUrls.updatePrototyperDemoLink(id);
      
      const payload: any = {};
      if (slug !== undefined) payload.slug = slug;

      const response = await apiClient.patch<PrototypeCreationResponse>(url, payload);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by slug
   */
  async getPrototyperBySlug(slug: string): Promise<PrototyperBySlugResponse> {
    try {
      const url = ApiUrls.getPrototyperBySlug(slug);
      const response = await apiClient.get<PrototyperBySlugResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Get prototyper demo link by ID
   */
  async getPrototyperById(id: string | number): Promise<PrototypeCreationResponse> {
    try {
      const url = ApiUrls.getPrototyperById(id);
      const response = await apiClient.get<PrototypeCreationResponse>(url);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error.response) {
      const message = error.response.data?.message || 'Failed to create prototype';
      return new Error(message);
    }

    // Network error
    return new Error('Network error. Please check your connection.');
  }
}

export const prototyperService = new PrototyperService();
export default prototyperService;
